import { describeClientError } from '@src/app/shared/utils/client-error-message';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  PartnerReferralItemResponse,
  PartnerReferralLinksResponse,
} from '@interfaces/partners/responses/partner-referral.response';
import { PartnerReferralsHttpService } from '@services/http/partners/partner-referrals-http.service';
import {
  referralChannelLabel,
  referralDetail,
  referralStatusLabel,
} from '@src/app/shared/utils/partner-referral-link.utils';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { SharedModule } from '@src/app/shared/shared.module';

/**
 * Aba "Indicações" da tela interna do parceiro (Comercial › Parceiros, card 3461).
 *
 * Mostra quem indicou este parceiro (ou "Cadastro direto, sem indicação") e os parceiros que
 * se cadastraram pelos links dos usuários dele. Só leitura. Quem decide se a aba aparece é a
 * tela do parceiro, pela permissão `PARTNER_VIEW_REFERRALS`; o endpoint revalida.
 *
 * Os nomes não são link para o outro parceiro de propósito: a tela de edição lê o `:id` só do
 * snapshot da rota, então navegar de um parceiro para outro reaproveitaria o componente com os
 * dados do primeiro.
 */
@Component({
  selector: 'app-partner-referrals',
  standalone: true,
  imports: [CommonModule, SharedModule, VaultDateTimePipe],
  templateUrl: './partner-referrals.component.html',
  styleUrls: ['./partner-referrals.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerReferralsComponent implements OnChanges {
  private readonly referralsService = inject(PartnerReferralsHttpService);

  readonly partnerId = input.required<string>();

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly links = signal<PartnerReferralLinksResponse | null>(null);

  readonly referred = computed<PartnerReferralItemResponse[]>(() => this.links()?.referred ?? []);

  readonly columnDefinitions: ColumnDefinition[] = [
    new ColumnDefinition({ field: 'partnerName', header: 'Parceiro' }),
    new ColumnDefinition({ field: 'channel', header: 'Via' }),
    new ColumnDefinition({ field: 'referredAt', header: 'Quando' }),
    new ColumnDefinition({ field: 'status', header: 'Status' }),
  ];

  readonly channelLabel = referralChannelLabel;
  readonly statusLabel = referralStatusLabel;
  readonly detail = referralDetail;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['partnerId']) this.load();
  }

  load(): void {
    const id = this.partnerId();
    if (!id) return;

    this.loading.set(true);
    this.errorMessage.set(null);

    this.referralsService.getPartnerReferrals(id).subscribe({
      next: response => {
        this.links.set(response ?? { referredBy: null, referred: [] });
        this.loading.set(false);
      },
      error: error => {
        this.loading.set(false);
        this.errorMessage.set(
          describeClientError(
            error,
            'Não conseguimos carregar as indicações deste parceiro agora. Clique em Tentar de novo.'
          )
        );
      },
    });
  }
}
